import type { TElement } from 'platejs';

import {
    ParagraphPlugin,
    useEditorRef,
    useSelectionFragmentProp,
} from 'platejs/react';
import FontAwesome from "../../../utilities/FontAwesome";
import {getBlockType, toggleBlock} from "./utils";
import {Popover} from "radix-ui";
import styles from "../../styles/SlateEditor.module.scss";
import {NodeType} from "../../utils";
import {ButtonWrapper} from "./_Button";
import {useMemo, useRef, useState} from "react";
import { toggleColumnGroup } from '@platejs/layout';
import {DoubleColumnOutlined} from "../../elements/Columns";

const ACTION_TWO_COLUMNS = "action_two_columns";

const turnIntoItems = [
    {
        icon: <FontAwesome fixedWidth prefix={'fas'} name={'fa-paragraph'} />,
        keywords: ['paragraph'],
        label: 'Text',
        value: NodeType.PARAGRAPH,
    },
    {
        icon: <FontAwesome fixedWidth prefix={'fas'} name={'fa-heading'} />,
        keywords: ['title', 'h1'],
        label: 'Heading 1',
        value: NodeType.HEADING_ONE,
    },
    {
        icon: <FontAwesome fixedWidth prefix={'fas'} name={'fa-heading'} />,
        keywords: ['subtitle', 'h2'],
        label: 'Heading 2',
        value: NodeType.HEADING_TWO,
    },
    {
        icon: <FontAwesome fixedWidth prefix={'fas'} name={'fa-heading'} />,
        keywords: ['subtitle', 'h3'],
        label: 'Heading 3',
        value: NodeType.HEADING_THREE,
    },
    {
        icon: <FontAwesome fixedWidth prefix={'fas'} name={'fa-heading'} />,
        keywords: ['subtitle', 'h4'],
        label: 'Heading 4',
        value: NodeType.HEADING_FOUR,
    },
    {
        icon: <FontAwesome fixedWidth prefix={'fas'} name={'fa-list-ul'} />,
        keywords: ['unordered', 'ul', '-'],
        label: 'Bulleted list',
        value: NodeType.UNORDERED_LIST,
    },
    {
        icon: <FontAwesome fixedWidth prefix={'fas'} name={'fa-list-ol'} />,
        keywords: ['ordered', 'ol', '1'],
        label: 'Numbered list',
        value: NodeType.ORDERED_LIST,
    },
    {
        icon: <FontAwesome fixedWidth prefix={'fas'} name={'fa-quote-left'} />,
        keywords: ['citation', 'blockquote', '>'],
        label: 'Quote',
        value: NodeType.BLOCK_QUOTE,
    },
    {
        icon: <span style={{verticalAlign: "middle", display: "inline-block", paddingRight: 3}}>
            <DoubleColumnOutlined />
        </span>,
        label: '2 Columns',
        value: ACTION_TWO_COLUMNS,
    }
];

export function TurnIntoDropdownMenu(props) {
    const editor = useEditorRef();
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);

    const value = useSelectionFragmentProp({
        defaultValue: ParagraphPlugin.key,
        getProp: (node) => getBlockType(node as TElement),
    });
    const selectedItem = useMemo(
        () =>
            turnIntoItems.find(
                (item) => item.value === (value ?? ParagraphPlugin.key)
            ) ?? turnIntoItems[0],
        [value]
    );

    return (
        <div className={styles.turnIntoDropdownWrapper}>
            <Popover.Root open={open}>
                <Popover.Trigger asChild>
                    <ButtonWrapper ref={triggerRef} onClick={(e) => {
                        e.preventDefault();
                        setOpen(v => !v)
                    }}>
                        <div>
                            {selectedItem.label}
                        </div>
                        <FontAwesome fixedWidth prefix={'fas'} name={'fa-chevron-down'} />
                    </ButtonWrapper>
                </Popover.Trigger>
                <Popover.Content
                    className={styles.turnIntoDropdownContent}
                    onPointerDownOutside={(e) => {
                        if (triggerRef.current && !triggerRef.current.contains(e.target as HTMLElement)) {
                            setOpen(false)
                        }
                    }}
                >
                    {turnIntoItems.map(({ icon, label, value: itemValue }) => (
                        <div className={styles.row} key={itemValue}>
                            <button onClick={e => {
                                if (itemValue === ACTION_TWO_COLUMNS) {
                                    toggleColumnGroup(editor, {widths: ["50%", "50%"]})
                                } else {
                                    toggleBlock(editor, itemValue)
                                }
                                setOpen(false);
                            }}>
                                {icon}
                                {label}
                            </button>
                        </div>
                    ))}
                </Popover.Content>
            </Popover.Root>
        </div>
    );
}
